import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { AnimatedSection } from '../common/AnimatedSection';

interface Testimonial {
  name: string;
  quote: string;
  service: string;
  avatar: string;
}

const testimonials: Testimonial[] = [
  {
    name: 'Sophie Martin',
    quote:
      'Un service exceptionnel ! Le chauffeur était ponctuel, discret et très professionnel. La voiture était impeccable. Je recommande vivement pour tous vos déplacements.',
    service: 'Transfert Aéroport',
    avatar: 'https://i.pravatar.cc/150?img=47',
  },
  {
    name: 'Jean-Pierre Dubois',
    quote:
      "J'utilise Touré VTC régulièrement pour mes déplacements professionnels. La qualité de service est constante et irréprochable. Un vrai partenaire de confiance.",
    service: 'Déplacements Professionnels',
    avatar: 'https://i.pravatar.cc/150?img=12',
  },
  {
    name: 'Marie Lefebvre',
    quote:
      'Pour notre mariage, nous avions besoin d\u2019un service à la hauteur. Touré VTC a dépassé toutes nos attentes. Élégance et ponctualité parfaites.',
    service: 'Événement Spécial',
    avatar: 'https://i.pravatar.cc/150?img=23',
  },
  {
    name: 'Thomas Bernard',
    quote:
      "Excellent service de transfert vers Charles de Gaulle. Le chauffeur connaissait parfaitement l'itinéraire et m'a déposé bien en avance. Merci !",
    service: 'Transfert CDG',
    avatar: 'https://i.pravatar.cc/150?img=33',
  },
  {
    name: 'Isabelle Moreau',
    quote:
      "Une expérience de transport vraiment premium. L'intérieur de la voiture est toujours propre et le chauffeur aux petits soins. Service cinq étoiles.",
    service: 'Mise à Disposition',
    avatar: 'https://i.pravatar.cc/150?img=44',
  },
  {
    name: 'Laurent Petit',
    quote:
      "Professionnel, courtois et toujours à l'heure. C'est le service VTC que je recommande à tous mes collègues et partenaires d'affaires.",
    service: 'Corporate',
    avatar: 'https://i.pravatar.cc/150?img=53',
  },
  {
    name: 'Nathalie Roux',
    quote:
      'Premier voyage avec Touré VTC et certainement pas le dernier. Le confort de la voiture et la conduite souple rendent le trajet très agréable.',
    service: 'Longue Distance',
    avatar: 'https://i.pravatar.cc/150?img=26',
  },
  {
    name: 'François Durand',
    quote:
      'Service impeccable pour un circuit touristique dans Paris. Le chauffeur est un véritable guide qui connaît la capitale sur le bout des doigts.',
    service: 'Circuit Touristique',
    avatar: 'https://i.pravatar.cc/150?img=59',
  },
  {
    name: 'Caroline Leroy',
    quote:
      'Je fais appel à Touré VTC pour tous mes déplacements depuis un an. La fiabilité et le professionnalisme sont au rendez-vous à chaque fois.',
    service: 'Client Régulier',
    avatar: 'https://i.pravatar.cc/150?img=20',
  },
  {
    name: 'Philippe Girard',
    quote:
      "Un service de grande qualité. Du premier contact à la fin du trajet, tout est parfaitement organisé. Le rapport qualité-prix est excellent.",
    service: 'Transfert Gare',
    avatar: 'https://i.pravatar.cc/150?img=60',
  },
];

const AUTOPLAY_INTERVAL = 5000;
/** Seuil en px pour considérer un swipe/drag comme intentionnel */
const DRAG_THRESHOLD = 50;

/* ---------- styled-components ---------- */

const Section = styled.section`
  padding: var(--spacing-xl, 5rem) 0;
  background-color: var(--primary-color, #0a0a0a);
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: var(--spacing-lg, 3rem) 0;
  }
`;

const TopDivider = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 120px;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--accent, #d4af37), transparent);
`;

const BottomDivider = styled(TopDivider)`
  top: auto;
  bottom: 0;
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-sm, 1rem);
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: var(--spacing-lg, 3rem);
`;

const Title = styled.h2`
  font-family: 'Playfair Display', serif;
  color: var(--text-light, #fff);
  margin-bottom: var(--spacing-sm, 1rem);
  position: relative;
  display: inline-block;
  padding-bottom: var(--spacing-sm, 1rem);

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: var(--accent, #d4af37);
  }
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.05rem;
  max-width: 600px;
  margin: var(--spacing-md, 1.5rem) auto 0;
  line-height: 1.6;
`;

const CarouselViewport = styled.div`
  overflow: hidden;
  position: relative;
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(40px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const Track = styled.div<{ $offset: number; $animated: boolean }>`
  display: flex;
  transition: ${({ $animated }) =>
    $animated ? 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none'};
  transform: translateX(${({ $offset }) => $offset}%);
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
`;

const Slide = styled.div<{ $columns: number }>`
  flex: 0 0 ${({ $columns }) => 100 / $columns}%;
  padding: 0 0.75rem;
  box-sizing: border-box;
  animation: ${slideIn} 0.5s ease both;

  @media (max-width: 1024px) {
    flex: 0 0 50%;
  }

  @media (max-width: 640px) {
    flex: 0 0 100%;
  }
`;

const Card = styled.div`
  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 0.05) 0%,
    rgba(255, 255, 255, 0.02) 100%
  );
  border: 1px solid rgba(212, 175, 55, 0.15);
  border-radius: var(--border-radius-md, 12px);
  padding: 2rem 1.75rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    border-color: rgba(212, 175, 55, 0.35);
    box-shadow: 0 8px 32px rgba(212, 175, 55, 0.08);
  }
`;

const QuoteMark = styled.span`
  font-family: 'Playfair Display', serif;
  font-size: 4rem;
  line-height: 1;
  color: rgba(212, 175, 55, 0.2);
  position: absolute;
  top: 0.75rem;
  left: 1.25rem;
  user-select: none;
`;

const QuoteText = styled.blockquote`
  font-family: 'Montserrat', sans-serif;
  font-style: italic;
  font-size: 0.925rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.8);
  margin: 1.5rem 0 1.25rem;
  flex-grow: 1;
`;

const Author = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: auto;
`;

const Avatar = styled.img`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 2px solid var(--accent, #d4af37);
  object-fit: cover;
`;

const AuthorInfo = styled.div``;

const AuthorName = styled.span`
  display: block;
  font-family: 'Playfair Display', serif;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-light, #fff);
`;

const ServiceType = styled.span`
  display: block;
  font-size: 0.75rem;
  color: var(--accent, #d4af37);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 2px;
`;

const Dots = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: var(--spacing-lg, 2.5rem);
`;

const Dot = styled.button<{ $active: boolean }>`
  width: ${({ $active }) => ($active ? '28px' : '8px')};
  height: 8px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  background-color: ${({ $active }) =>
    $active ? 'var(--accent, #d4af37)' : 'rgba(255,255,255,0.25)'};
  transition: all 0.3s ease;
  padding: 0;

  &:hover {
    background-color: ${({ $active }) =>
    $active ? 'var(--accent, #d4af37)' : 'rgba(255,255,255,0.45)'};
  }
`;

/* ---------- helpers ---------- */

function useColumns() {
  const [cols, setCols] = useState(3);

  useEffect(() => {
    function update() {
      if (window.innerWidth <= 640) setCols(1);
      else if (window.innerWidth <= 1024) setCols(2);
      else setCols(3);
    }
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return cols;
}

/* ---------- component ---------- */

export function TestimonialsSection() {
  const columns = useColumns();
  const totalPages = Math.ceil(testimonials.length / columns);

  /**
   * Boucle infinie : on duplique les slides (clone avant + clone après).
   * L'index réel commence à totalPages (après les clones de fin).
   * Quand on atteint un clone, on téléporte silencieusement sans animation.
   */
  const [page, setPage] = useState(totalPages); // démarre sur la vraie page 0 (après les clones)
  const [animated, setAnimated] = useState(true);
  const [paused, setPaused] = useState(false);
  const isTransitioning = useRef(false);

  // Drag / swipe state
  const dragStartX = useRef<number | null>(null);
  const dragDelta = useRef(0);
  const isDragging = useRef(false);

  /** Pages affichées : [clones fin] + [vraies] + [clones début] */
  /** Offset en % basé sur la page courante dans le tableau étendu */
  const slideWidth = 100 / columns;
  const itemsPerPage = columns;
  const offset = -(page * itemsPerPage * slideWidth);

  /** Navigue vers une page (logique infinie) */
  const goToRaw = useCallback((p: number) => {
    if (isTransitioning.current) return;
    isTransitioning.current = true;
    setAnimated(true);
    setPage(p);
  }, []);

  /** Après transition : téléporter si on est sur un clone */
  const handleTransitionEnd = useCallback(() => {
    isTransitioning.current = false;
    setPage((p) => {
      // Clone de début atteint (on va vers la gauche au-delà de 0)
      if (p < totalPages) {
        setAnimated(false);
        return p + totalPages;
      }
      // Clone de fin atteint (on va vers la droite au-delà des vrais)
      if (p >= totalPages * 2) {
        setAnimated(false);
        return p - totalPages;
      }
      return p;
    });
  }, [totalPages]);

  /** Index logique pour les dots (0 à totalPages-1) */
  const dotIndex = ((page - totalPages) % totalPages + totalPages) % totalPages;

  /** Auto-play */
  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => goToRaw(page + 1), AUTOPLAY_INTERVAL);
    return () => clearInterval(timer);
  }, [page, paused, goToRaw]);

  /** Reset si colonnes changent */
  useEffect(() => {
    setAnimated(false);
    setPage(totalPages);
  }, [columns, totalPages]);

  /* ---- Drag / Swipe handlers ---- */

  const handlePointerDown = useCallback((clientX: number) => {
    dragStartX.current = clientX;
    dragDelta.current = 0;
    isDragging.current = true;
    setPaused(true);
  }, []);

  const handlePointerMove = useCallback((clientX: number) => {
    if (!isDragging.current || dragStartX.current === null) return;
    dragDelta.current = clientX - dragStartX.current;
  }, []);

  const handlePointerUp = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const delta = dragDelta.current;
    if (Math.abs(delta) >= DRAG_THRESHOLD) {
      if (delta < 0) goToRaw(page + 1);
      else goToRaw(page - 1);
    }
    dragStartX.current = null;
    dragDelta.current = 0;
    // Reprend l'auto-play après un court délai
    setTimeout(() => setPaused(false), AUTOPLAY_INTERVAL);
  }, [page, goToRaw]);

  /* Construire le tableau de slides étendu pour la boucle infinie */
  const extendedSlides: Testimonial[] = [
    ...testimonials, // clones de fin (avant)
    ...testimonials, // vraies slides
    ...testimonials, // clones de début (après)
  ];

  /** Téléporter sans animation si nécessaire (après changement de colonnes) */
  useEffect(() => {
    if (!animated) {
      // Force re-render avec animation désactivée puis réactivée
      const t = setTimeout(() => setAnimated(true), 50);
      return () => clearTimeout(t);
    }
  }, [animated]);

  return (
    <Section id="avis">
      <TopDivider />
      <BottomDivider />

      <Inner>
        <Header>
          <AnimatedSection>
            <Title>Ce Que Disent Nos Clients</Title>
            <Subtitle>
              La satisfaction de nos clients est notre plus grande fierté.
              Découvrez leurs témoignages.
            </Subtitle>
          </AnimatedSection>
        </Header>

        <AnimatedSection delay={1}>
          <CarouselViewport
            /* Pause au clic/appui */
            onMouseDown={(e) => handlePointerDown(e.clientX)}
            onMouseMove={(e) => handlePointerMove(e.clientX)}
            onMouseUp={handlePointerUp}
            onMouseLeave={handlePointerUp}
            /* Touch swipe */
            onTouchStart={(e) => handlePointerDown(e.touches[0].clientX)}
            onTouchMove={(e) => handlePointerMove(e.touches[0].clientX)}
            onTouchEnd={handlePointerUp}
            /* Empêche la sélection de texte pendant le drag */
            style={{ touchAction: 'pan-y' }}
          >
            <Track
              $offset={offset}
              $animated={animated}
              onTransitionEnd={handleTransitionEnd}
            >
              {extendedSlides.map((t, i) => (
                <Slide key={i} $columns={columns}>
                  <Card>
                    <QuoteMark>&ldquo;</QuoteMark>
                    <QuoteText>{t.quote}</QuoteText>
                    <Author>
                      <Avatar
                        src={t.avatar}
                        alt={t.name}
                        loading="lazy"
                        width={52}
                        height={52}
                        draggable={false}
                      />
                      <AuthorInfo>
                        <AuthorName>{t.name}</AuthorName>
                        <ServiceType>{t.service}</ServiceType>
                      </AuthorInfo>
                    </Author>
                  </Card>
                </Slide>
              ))}
            </Track>
          </CarouselViewport>
        </AnimatedSection>

        <Dots>
          {Array.from({ length: totalPages }).map((_, i) => (
            <Dot
              key={i}
              $active={i === dotIndex}
              onClick={() => {
                goToRaw(i + totalPages);
                setPaused(false);
              }}
              aria-label={`Page ${i + 1}`}
            />
          ))}
        </Dots>
      </Inner>
    </Section>
  );
}

export default TestimonialsSection;
